import { parseApiDateUtc } from '@/lib/api-date'
import type { TicketListItem } from '@/types/api'

function sortByUpdated(tickets: TicketListItem[], order: 'asc' | 'desc'): TicketListItem[] {
  return [...tickets].sort((a, b) => {
    const da = parseApiDateUtc(a.last_update_at || a.updated_at || a.created_at)?.getTime() ?? NaN
    const db = parseApiDateUtc(b.last_update_at || b.updated_at || b.created_at)?.getTime() ?? NaN
    if (Number.isNaN(da) && Number.isNaN(db)) return 0
    if (Number.isNaN(da)) return 1
    if (Number.isNaN(db)) return -1
    return order === 'asc' ? da - db : db - da
  })
}

export type TicketsDisplayGroups = {
  assigned_to_me: TicketListItem[]
  unassigned: TicketListItem[]
  /** Порядок ключей — порядок первого появления на странице (как на бэкенде) */
  assigned_to_others: Record<string, TicketListItem[]>
}

/**
 * Как `groupTicketsForDisplay` в `frontend/src/pages/Tickets.js`: блоки «мне», «не назначены», «на других».
 */
export function groupTicketsForDisplay(
  tickets: TicketListItem[] | null | undefined,
  currentUserId: string | undefined,
  options: {
    /** Сортировка внутри блока по дате обновления (как в колонке «Обновлён») */
    dateSortOrder?: 'asc' | 'desc'
    /**
     * Если false — сохраняем порядок строк с API (нужно при сортировке по другим колонкам).
     * Если true — внутри каждого блока дополнительно сортируем по дате обновления.
     */
    resortWithinGroupsByUpdated?: boolean
  } = {},
): TicketsDisplayGroups {
  const list = Array.isArray(tickets) ? tickets : []
  const dateSortOrder = options.dateSortOrder ?? 'asc'
  const resort = options.resortWithinGroupsByUpdated !== false

  const groups: TicketsDisplayGroups = {
    assigned_to_me: [],
    unassigned: [],
    assigned_to_others: {},
  }

  for (const ticket of list) {
    const delegatedToId = ticket.delegated_to_operator_id
      ? String(ticket.delegated_to_operator_id)
      : null
    // Новый API: delegated_to — кому временно переведено.
    // Старый: delegated_from отдавался только делегату → считаем «мне».
    const delegatedToMe = Boolean(
      (delegatedToId && currentUserId && delegatedToId === String(currentUserId)) ||
        (ticket.delegated_from_operator_id && !delegatedToId),
    )
    if (ticket.assigned_to_id === currentUserId || delegatedToMe) {
      groups.assigned_to_me.push(ticket)
    } else if (!ticket.assigned_to_id && !delegatedToId) {
      groups.unassigned.push(ticket)
    } else {
      // Перераспределённые показываем у временного получателя — для всех зрителей.
      const op = delegatedToId || ticket.assigned_to_id
      if (!op) continue
      if (!groups.assigned_to_others[op]) groups.assigned_to_others[op] = []
      groups.assigned_to_others[op].push(ticket)
    }
  }

  if (resort) {
    groups.assigned_to_me = sortByUpdated(groups.assigned_to_me, dateSortOrder)
    groups.unassigned = sortByUpdated(groups.unassigned, dateSortOrder)
    for (const k of Object.keys(groups.assigned_to_others)) {
      groups.assigned_to_others[k] = sortByUpdated(groups.assigned_to_others[k], dateSortOrder)
    }
  }

  return groups
}
